import React, { useEffect, useState } from 'react';
import {
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';
import { fpColors } from '../../../styles/theme';
import { PrimaryCTA } from '../../../components/PrimaryCTA';
import { api } from '../../../api/remembrApi';
import type { InventoryItemResponse } from '../../../api/types';

/**
 * PriceEditSheet — Stage 2D polish.
 *
 * Single-purpose modal for updating an item's `estimated_value`.
 * Triggered by tapping the PRICE block on ItemDetailScreen. Wraps
 * the existing PATCH /users/inventory/{id} endpoint.
 *
 * UX: dollar prefix glued to a numeric input; trims commas + non-
 * numeric chars on save. Empty → null (clears the field).
 */

interface PriceEditSheetProps {
  visible: boolean;
  item: InventoryItemResponse;
  onClose: () => void;
  onSaved: (updated: InventoryItemResponse) => void;
}

export const PriceEditSheet: React.FC<PriceEditSheetProps> = ({
  visible,
  item,
  onClose,
  onSaved,
}) => {
  const initial =
    typeof item.estimated_value === 'number'
      ? item.estimated_value.toFixed(2)
      : '';
  const [value, setValue] = useState<string>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setValue(initial);
      setError(null);
    }
  }, [visible, initial]);

  /** Parse user input. Strips currency symbol, commas, whitespace.
   *  Returns null when the field is empty (clears price) OR a
   *  positive number; throws on invalid input. */
  const parsePrice = (raw: string): number | null => {
    const cleaned = raw.replace(/[$,\s]/g, '').trim();
    if (!cleaned) return null;
    const n = Number(cleaned);
    if (!Number.isFinite(n) || n < 0) {
      throw new Error('Enter a valid amount (e.g. 12.50)');
    }
    return n;
  };

  const handleSave = async () => {
    if (!item.id) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let parsed: number | null;
      try {
        parsed = parsePrice(value);
      } catch (validationErr: any) {
        setError(validationErr.message || 'Invalid amount');
        setSaving(false);
        return;
      }
      // Backend accepts `estimated_value: number | null` per the
      // existing UpdateInventoryItemRequest schema.
      const updated = await api.updateInventoryItem(item.id, {
        estimated_value: parsed ?? undefined,
      });
      const merged: InventoryItemResponse = {
        ...item,
        ...updated,
        estimated_value: parsed ?? undefined,
      } as InventoryItemResponse;
      Toast.show({ type: 'success', text1: 'Price saved' });
      onSaved(merged);
      onClose();
    } catch (err: any) {
      const detailRaw = err?.response?.data?.detail;
      const detail = Array.isArray(detailRaw)
        ? detailRaw.map((d: any) => d?.msg || JSON.stringify(d)).join(', ')
        : typeof detailRaw === 'string'
          ? detailRaw
          : null;
      setError(detail || err?.message || 'Could not save price.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View
          style={styles.sheet}
          accessible
          accessibilityViewIsModal
          accessibilityLabel="Update price"
          // @ts-ignore — RN Web only
          role="dialog"
          aria-modal={true}
          testID="price-edit-sheet"
        >
          <View style={styles.header}>
            <Text style={styles.title}>Update price</Text>
            <Pressable
              onPress={onClose}
              hitSlop={8}
              accessibilityLabel="Close"
              accessibilityRole="button"
              style={styles.closeBtn}
              testID="price-edit-close"
            >
              <MaterialIcons name="close" size={22} color={fpColors.onSurface} />
            </Pressable>
          </View>

          <View style={styles.body}>
            <Text style={styles.fieldLabel}>
              What&apos;s the price of &ldquo;{item.item_name ?? 'this item'}&rdquo;?
            </Text>
            <View style={styles.inputWrap}>
              <Text style={styles.inputPrefix}>$</Text>
              <TextInput
                value={value}
                onChangeText={setValue}
                onSubmitEditing={handleSave}
                returnKeyType="done"
                keyboardType="decimal-pad"
                placeholder="0.00"
                placeholderTextColor={fpColors.outline}
                style={styles.input}
                autoFocus
                accessibilityLabel="Price amount"
                testID="price-edit-input"
              />
            </View>
            <Text style={styles.helper}>
              Leave empty to clear the price.
            </Text>

            {error ? (
              <View style={styles.errorBanner} testID="price-edit-error">
                <MaterialIcons
                  name="error-outline"
                  size={16}
                  color={fpColors.error}
                />
                <Text style={styles.errorText}>{error}</Text>
              </View>
            ) : null}
          </View>

          <View style={styles.footer}>
            <Pressable
              onPress={onClose}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Cancel"
              style={styles.cancelBtn}
              testID="price-edit-cancel"
            >
              <Text style={styles.cancelText}>Cancel</Text>
            </Pressable>
            <View style={styles.saveBtnWrap} testID="price-edit-save">
              <PrimaryCTA
                label={saving ? 'Saving…' : 'Save'}
                onPress={handleSave}
                disabled={saving}
              />
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(28, 23, 33, 0.55)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: fpColors.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingBottom: Platform.OS === 'ios' ? 24 : 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 18,
    paddingBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: fpColors.onSurface,
  },
  closeBtn: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 16,
  },
  body: {
    padding: 20,
    gap: 8,
  },
  fieldLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
    marginBottom: 4,
  },
  inputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLow,
    paddingHorizontal: 14,
  },
  inputPrefix: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: fpColors.primary,
    marginRight: 8,
  },
  input: {
    flex: 1,
    paddingVertical: 12,
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: fpColors.onSurface,
  },
  helper: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.outline,
    marginTop: 2,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: `${fpColors.errorContainer}40`,
    borderRadius: 10,
    marginTop: 12,
  },
  errorText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.error,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: fpColors.outlineVariant,
  },
  cancelBtn: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
  },
  cancelText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurfaceVariant,
  },
  saveBtnWrap: { flex: 1 },
});

export default PriceEditSheet;
